import React, { useState, useMemo, useCallback, useEffect } from 'react';
import RDG from 'react-data-grid';
import styled from 'styled-components';

const GridTheme = styled.div`
    .react-grid-Container,
    .rdg {
        color: #212529 !important;
        height: ${props => (props.hasRows ? 'inherit !important' : '5.313rem !important')};
        overflow-y: ${props => (props.hasRows ? props.overflowY : 'hidden')} !important;
        overflow-x: ${props => props.overflowX} !important;
        border: 1px solid #dee2e6 !important;
    }

    .react-grid-Container::-webkit-scrollbar {
        height: 12px;
    }

    .rdg-header-row,
    .react-grid-Header,
    .react-grid-HeaderRow,
    .react-grid-HeaderCell,
    .react-grid-HeaderCell-sortable,
    .react-grid-HeaderCell--frozen{
        background-color: #43a2ef !important;
        color: #fff !important;
        line-height: unset !important;
    }

    .react-grid-HeaderCell * ,
    .react-grid-HeaderCell-sortable *{
        color: #fff !important;
    }

    .react-grid-HeaderCell .badge.badge-pill.badge-info {
        color: #212529 !important;
    }

    .rdg-header-row .rdg-cell,
    .react-grid-HeaderCell,
    .react-grid-HeaderCell-sortable,
    .react-grid-HeaderCell--frozen{
        border-bottom: 2px solid #dee2e6 !important;
        border-right: 1px solid rgba(255, 255, 255, 0.55) !important;
        height: 35px !important;
        line-height: 35px !important;
        font-size: 13px !important;
    }

    .react-grid-HeaderCell,
    .react-grid-HeaderCell--frozen {
        display: flex !important;
        align-items: center !important;
    }

    .react-grid-HeaderCell-sortable {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        width: auto !important;
        padding-right: 0 !important;
    }

    .react-grid-HeaderRow,
    .react-grid-Header {
        overflow: hidden !important;
        height: 35px !important;
    }

    .react-grid-HeaderCell-sortable .pull-right {
        font-size: 12px !important;
        margin-left: 6px !important;
        line-height: normal !important;
        float: none !important;
        display: inline-flex !important;
        align-items: center !important;
    }

    .react-grid-Grid {
        border: 1px solid #d3d3d3 !important;
    }

    .rdg-row{
        line-height: unset !important;
    }

    @media (min-width:1400px){
        .rdg-row{
            contain: unset !important;
        }
    }
    .rdg-row-even,
    .react-grid-Row:nth-child(even) .react-grid-Cell{
        background-color: rgba(0,0,0,.05);
    }

    .rdg-cell,
    .react-grid-Cell{
        padding: .3rem;
        line-height: normal !important;
        -webkit-user-select: text;
        -moz-user-select: text;
        -ms-user-select: text;
        user-select: text;
    }   

    .rdg-row .rdg-cell,
    .react-grid-Row .react-grid-Cell {
        display: flex;
        align-items: center;
    }

    .rdg-row-selected,
    .react-grid-Row.row-selected .react-grid-Cell{
        background-color: #b8daff !important;
        border-color: #7abaff;
        .rdg-cell{
            border-color: #7abaff;
        }       
    }

    .rdg-cell-selected{
        box-shadow: unset !important;
    }
`;

const BigGrid = (defaultProps) => {

    const props = {
        className: '',
        onScroll: () => { },
        onRowClick: () => { },
        overflowX: 'scroll',
        overflowY: 'scroll',
        ...defaultProps
    }

    const SortType = {
        NONE: 'NONE',
        ASC: 'ASC',
        DESC: 'DESC'
    };

    const [sort, setSort] = useState({ sortColumn: 'primarySerialNumber', sortDirection: SortType.NONE });
    const [selectedRows, setSelectedRows] = useState(new Set([]));

    const rows = [...props.data].map((item, index) => ({ rowIndex: index, ...item }));

    const columns = [...props.columns].map((item) => {
        const sortable = !item.disableSortBy;
        const column = {
            name: item.Header,
            key: item.accessor,
            sortable: sortable,
            ...item,
        }

        if (typeof item.Header === 'function') {
            column.headerRenderer = () => <div >{item.Header()}</div>;
        }

        if (item.Cell) {
            column.formatter = (cellInfo) => {
                const resolvedRow =
                    cellInfo.rowData ||
                    cellInfo.row ||
                    (typeof cellInfo.rowIdx === 'number' ? rows[cellInfo.rowIdx] : undefined) ||
                    {};

                const originalRow = resolvedRow.original || resolvedRow;
                const value = cellInfo.value !== undefined ? cellInfo.value : getVal(originalRow, item.accessor);
                return item.Cell({ row: { original: originalRow }, cell: { value: value } });
            };
        }

        return column;
    });

    const className = 'rdg-light table table-bordered ' + props.className;

    const EmptyRowsView = () => {
        return <div className='p-1'>{props.noDataText}</div>;
    }

    const handleSort = useCallback((columnKey, direction) => {
        const mappedDirection = direction === 'NONE' ? SortType.NONE : direction;
        setSort({ sortColumn: columnKey, sortDirection: mappedDirection });
    }, []);

    const getVal = (obj, key) => {
        if (!obj) {
            return '';
        }

        if (key.indexOf('.') === -1) {
            return obj[key];
        }

        var parts = key.split('.');
        for (var i = 0; i < parts.length; ++i) {
            if (obj) {
                obj = obj[parts[i]];
            }
        }
        return obj;
    }

    const normalizeSortValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return { rank: 1, value: '' };
        }

        if (value instanceof Date) {
            return { rank: 0, value: value.getTime() };
        }

        if (typeof value === 'number') {
            return { rank: 0, value: value };
        }

        if (typeof value === 'boolean') {
            return { rank: 0, value: value ? 1 : 0 };
        }

        const parsedDate = Date.parse(value);
        if (!Number.isNaN(parsedDate) && /[-/]/.test(String(value))) {
            return { rank: 0, value: parsedDate };
        }

        const numericValue = Number(value);
        if (!Number.isNaN(numericValue) && String(value).trim() !== '') {
            return { rank: 0, value: numericValue };
        }

        return { rank: 0, value: String(value).toLowerCase() };
    }

    const sorter = (a, b, sortDir, sortKey) => {
        const aVal = normalizeSortValue(getVal(a, sortKey));
        const bVal = normalizeSortValue(getVal(b, sortKey));

        if (aVal.rank !== bVal.rank) {
            return aVal.rank - bVal.rank;
        }

        if (typeof aVal.value === 'string' && typeof bVal.value === 'string') {
            return aVal.value.localeCompare(bVal.value, undefined, { numeric: true, sensitivity: 'base' });
        }

        if (aVal.value > bVal.value) {
            return 1;
        }

        if (aVal.value < bVal.value) {
            return -1;
        }

        return 0;
    }

    const sortedRows = useMemo(() => {
        if (sort.sortDirection === SortType.NONE) return rows;

        let sortedRows = [...rows];
        sortedRows = sortedRows.sort((a, b) => sorter(a, b, sort.sortDirection, sort.sortColumn));
        return sort.sortDirection === SortType.DESC ? sortedRows.reverse() : sortedRows;

    }, [rows, sort.sortDirection, sort.sortColumn]);

    const setSortIcons = useCallback(() => {
        const sortableColumns = columns.filter(column => column.sortable);
        const headerNodes = [...document.getElementsByClassName('react-grid-HeaderCell-sortable')];

        headerNodes.forEach((item, index) => {
            const column = sortableColumns[index];
            if (!column) {
                return;
            }

            let iconNode = item.querySelector('.pull-right');
            if (!iconNode) {
                iconNode = document.createElement('span');
                iconNode.className = 'pull-right';
                item.appendChild(iconNode);
            }
            else {
                // Keep icon consistently after header text.
                item.appendChild(iconNode);
            }

            let icon = iconNode.querySelector('i');
            if (!icon) {
                icon = document.createElement('i');
                iconNode.appendChild(icon);
            }

            if (sort.sortColumn === column.key && sort.sortDirection === SortType.ASC) {
                icon.className = 'fas fa-sort-amount-down-alt';
            }
            else if (sort.sortColumn === column.key && sort.sortDirection === SortType.DESC) {
                icon.className = 'fas fa-sort-amount-up-alt';
            }
            else {
                icon.className = 'fa fa-sort';
            }
            iconNode.setAttribute('aria-hidden', 'true');
        });
    }, [columns, sort.sortColumn, sort.sortDirection]);

    const onScroll = (event) => {
        setSortIcons();
        props.onScroll(event);
    }

    useEffect(() => {
        const timer = setTimeout(setSortIcons, 50);
        return () => clearTimeout(timer);
    }, [setSortIcons, sort.sortColumn, sort.sortDirection, sortedRows.length]);

    const onRowClick = (rowIdx, row) => {
        const clickedRow = row && row.rowIndex !== undefined
            ? row
            : typeof rowIdx === 'number'
                ? sortedRows[rowIdx]
                : rowIdx && rowIdx.rowIndex !== undefined
                    ? rowIdx
                    : undefined;

        if (!clickedRow || clickedRow.rowIndex === undefined) {
            return;
        }

        const selectedRowItems = new Set(selectedRows);
        if (selectedRowItems.has(clickedRow.rowIndex)) {
            selectedRowItems.delete(clickedRow.rowIndex)
        }
        else {
            if (!props.allowMultiSelect) {
                selectedRowItems.clear();
            }
            selectedRowItems.add(clickedRow.rowIndex)
        }
        setSelectedRows(selectedRowItems);
        const rowItems = [...selectedRowItems].map(i => {
            const item = { ...rows.find(r => r.rowIndex === i) };
            delete item.rowIndex;
            return item;
        });
        if (props.allowMultiSelect) {
            props.onRowClick(rowItems);
        }
        else {
            props.onRowClick(rowItems[0]);
        }
    }

    useEffect(() => {
        const selectedRowItems = selectedRows;
        switch (props.selectionType) {
            case 'all':
                selectedRowItems.clear();
                const rowIndexes = rows.map(r => r.rowIndex)
                setSelectedRows(new Set([...rowIndexes]));
                break;
            case 'none':
                selectedRowItems.clear();
                setSelectedRows(selectedRowItems);
                break;
            case 'some':
                break;
            default:
                break;
        }
    }, [props.selectionType]);

    return (
        <GridTheme hasRows={sortedRows.length > 0} overflowX={props.overflowX} overflowY={props.overflowY}>
            <RDG
                {...props}
                className={className}
                columns={columns}
                rowGetter={(index) => sortedRows[index]}
                rowsCount={sortedRows.length}
                minHeight={sortedRows.length ? 420 : 85}
                sortColumn={sort.sortColumn}
                sortDirection={sort.sortDirection}
                onGridSort={handleSort}
                onScroll={onScroll}
                emptyRowsView={EmptyRowsView}
                onRowClick={onRowClick}
            />
        </GridTheme>
    )
}

export default BigGrid;